import { i18n } from '@lingui/core';
import { msg, plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import type { ChannelTakeoverProposalEnvelope } from '@magig/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { ChannelIntakeDrawer } from '../components/channel-intake-drawer.js';
import { ConfirmDialog } from '../components/confirm-dialog.js';
import { DeleteSlotsChoice } from '../components/delete-slots-choice.js';
import { Mag } from '../components/mag.js';
import { SlotDrawer } from '../components/slot-drawer.js';
import { TopNav } from '../components/top-nav.js';
import type {
  Brand,
  BrandChannelDTO,
  BrandChannelKind,
  BrandContentPlanDTO,
  BrandContentSlotDTO,
} from '../lib/api.js';
import {
  approveChannelTakeoverProposal,
  type DesignerOption,
  discardChannelTakeoverProposal,
  disconnectMeta,
  fetchBrandDesigners,
  handOverBrandChannel,
  metaConnectStartUrl,
  patchBrandChannelSettings,
  pauseBrandChannel,
  proposeChannelTakeover,
  rebuildBrandChannelPlan,
  setBrandChannelOperatedByMag,
  unpauseBrandChannel,
} from '../lib/api.js';
import { useBrandTitle } from '../lib/document-title.js';
import { formatDate as formatDateIntl, formatTime } from '../lib/format.js';
import { createInvitation, InvitationApiError } from '../lib/invitations-api.js';
import { brandChannelDetailQuery, brandsQuery, meQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';

/**
 * Channel detail — `/brand/$slug/channels/$kind`. Spec §0b.6.
 *
 * Shows the active content plan (cadence + pillars) + the upcoming
 * scheduled slots. Read-only in 0b.6; write controls (pause,
 * operated_by_mag toggle) land in 0b.8.
 */

const KIND_SET = new Set<BrandChannelKind>(['instagram', 'linkedin', 'tiktok', 'youtube']);

/** [channel-intake] When set to 'open', the channel detail page mounts
 *  the ChannelIntakeDrawer on arrival. Used by the
 *  channel_onboarding_pending feed card deep-link so the founder lands
 *  inside the chat instead of having to click through the proposal
 *  CTA. Optional; absent = normal channel page.
 *
 *  Section query param drives which pane renders in the sidebar
 *  layout (control / content plan / slots). Mirrors the settings
 *  sub-route pattern but as a search param so we don't have to
 *  split this route into three files. Defaults to 'control'. */
const channelSectionSchema = z.enum(['control', 'plan', 'slots']);
type ChannelSection = z.infer<typeof channelSectionSchema>;
const channelDetailSearchSchema = z.object({
  intake: z.literal('open').optional(),
  section: channelSectionSchema.optional(),
  /** s216-54h.61 — when set, the page auto-opens the slot drawer
   *  for this slot id. used by the brand calendar to deep-link a
   *  day's draft straight into the review surface. cleared on
   *  drawer close. */
  slot: z.string().uuid().optional(),
});

export const Route = createFileRoute('/brand/$slug/channels/$kind')({
  validateSearch: (s) => channelDetailSearchSchema.parse(s),
  beforeLoad: async ({ params, context: { queryClient } }) => {
    const me = await queryClient.ensureQueryData(meQuery());
    if (!me) throw redirect({ to: '/login' });
    if (!KIND_SET.has(params.kind as BrandChannelKind)) {
      throw redirect({ to: '/brand/$slug/channels', params: { slug: params.slug } });
    }
    const brands = await queryClient.ensureQueryData(brandsQuery());
    const brand = brands.find((b) => b.slug === params.slug);
    if (!brand) throw redirect({ to: '/brands' });
    await queryClient.ensureQueryData(
      brandChannelDetailQuery(brand.id, params.kind as BrandChannelKind),
    );
    return { brand, kind: params.kind as BrandChannelKind };
  },
  component: ChannelDetailPage,
});

// Window during which a freshly-connected channel is presumed to be
// in onboarding (history-learn + content-plan agents firing in the
// background). After this the page falls back to the static "no plan
// yet" message so a stale state doesn't loop forever.
const ONBOARDING_WINDOW_MS = 5 * 60 * 1000;

/** Window after a takeover proposal is approved during which the
 *  background orchestrator (re-fired from the approve endpoint) is
 *  drafting the content plan. Same shape as ONBOARDING_WINDOW_MS but
 *  anchored on proposal.decidedAt instead of channel.connectedAt —
 *  the channel may have been connected long ago, with the founder
 *  just now approving mag's plan. */
const POST_APPROVAL_WINDOW_MS = 5 * 60 * 1000;

function ChannelDetailPage() {
  const { brand, kind } = Route.useRouteContext();
  const search = Route.useSearch();
  useBrandTitle(brand.slug, ['channels', kind]);
  // Local "retry just clicked" timestamp — surfaces a third drafting
  // window when the founder explicitly fires the orchestrator from
  // the PlanSection empty-state. Lives only on the client; resets on
  // navigation. Drives both the polling trigger AND the spinner.
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const { data } = useQuery({
    ...brandChannelDetailQuery(brand.id, kind),
    // Poll while onboarding is in flight so the plan/slots/cadence
    // appear as soon as the orchestrator lands them. Stops as soon
    // as we have a plan, OR the channel ages past the onboarding
    // window without one (something failed, fall back to static).
    //
    // [12.2L-173] Also poll while the auto-fired takeover proposal
    // is still generating — when the channel just connected and we
    // don't have an envelope yet, the background propose-takeover
    // call is in flight, so we want the page to pick it up the
    // moment it lands.
    refetchInterval: (query) => {
      const result = query.state.data;
      if (!result?.channel) return false;
      const now = Date.now();
      const connectedAtMs = result.channel.connectedAt
        ? new Date(result.channel.connectedAt).getTime()
        : null;
      const inOnboardingWindow =
        connectedAtMs !== null && now - connectedAtMs < ONBOARDING_WINDOW_MS;
      const proposalPending =
        !result.channel.operatedByMag && !result.takeoverProposal && inOnboardingWindow;
      // Post-approval window — the founder JUST approved mag's
      // takeover plan, server is firing the orchestrator. The
      // channel may have connectedAt from days ago so the
      // onboarding window won't cover this; key off decidedAt
      // instead. Polls until the plan lands or the window expires.
      const decidedAt =
        result.takeoverProposal?.status === 'approved' && result.takeoverProposal.decidedAt
          ? new Date(result.takeoverProposal.decidedAt).getTime()
          : null;
      const inPostApprovalWindow = decidedAt !== null && now - decidedAt < POST_APPROVAL_WINDOW_MS;
      const inRetryWindow = retryAt !== null && now - retryAt < POST_APPROVAL_WINDOW_MS;
      if (result.plan && !proposalPending) return false;
      if (inOnboardingWindow) return 3_000;
      if (inPostApprovalWindow) return 3_000;
      if (inRetryWindow) return 3_000;
      return false;
    },
    refetchIntervalInBackground: false,
  });

  const section: ChannelSection = search.section ?? 'control';
  // [channel-detail-disconnect] A brand_channels row exists for the
  // life of the brand once OAuth has landed once — disconnect flips
  // status to 'pending_connection' (not delete) so intake_state +
  // authority_tier + designer config survive. The detail page,
  // however, needs to render as NotConnected when the channel isn't
  // live, since "operating" / "let mag run this" / etc. controls
  // would mislead the founder about whether mag can act. Gate the
  // sidebar layout on status='active' instead of on row presence.
  const isLive = data?.channel?.status === 'active';
  return (
    <div className="min-h-dvh bg-[var(--paper)]">
      <TopNav active="channels" />
      <main className="mx-auto max-w-[1080px] px-4 py-8 sm:px-6 sm:py-10">
        <BackLink brand={brand} />
        <Header brand={brand} kind={kind} channel={data?.channel ?? null} />
        {data?.channel && isLive ? (
          <>
            {/* ProposalSection stays above the sidebar layout — it's
                a one-shot intake CTA, not a permanent surface, and
                disappears the moment the founder finalizes (operated
                flips). The sidebar pattern is for the steady state
                where mag is running the channel. */}
            {!data.channel.operatedByMag ? (
              <ProposalSection
                brand={brand}
                kind={kind}
                envelope={data.takeoverProposal ?? null}
                autoOpenIntake={search.intake === 'open'}
              />
            ) : null}
            <div className="mt-2 grid grid-cols-1 gap-7 md:grid-cols-[224px_1fr] md:gap-10">
              <ChannelRail brandSlug={brand.slug} kind={kind} active={section} />
              <section className="min-w-0">
                {section === 'control' ? (
                  <ControlSection brand={brand} kind={kind} channel={data.channel} />
                ) : null}
                {section === 'plan' ? (
                  <PlanSection
                    brand={brand}
                    kind={kind}
                    plan={data.plan ?? null}
                    channel={data.channel}
                    proposal={data.takeoverProposal ?? null}
                    slots={data.slots ?? []}
                    retryAt={retryAt}
                    onRetry={() => setRetryAt(Date.now())}
                  />
                ) : null}
                {section === 'slots' ? (
                  <SlotsSection
                    slots={data.slots ?? []}
                    brand={brand}
                    kind={kind}
                    channel={data.channel}
                    initialOpenSlotId={search.slot ?? null}
                  />
                ) : null}
              </section>
            </div>
          </>
        ) : (
          <NotConnected kind={kind} brand={brand} />
        )}
      </main>
    </div>
  );
}

/** [channel-detail-rail] Left sidebar mirroring the settings-page
 *  pattern (RAIL_ITEMS in brand.$slug.settings.tsx). Three sections —
 *  control / content plan / slots — each driven by the `section`
 *  search param so deep-links + browser back work normally.
 *
 *  Same active-state treatment as the settings rail: spell-yellow
 *  chiclet, ink border, plum drop shadow, right arrow. Glyphs
 *  match the line weight + corner rounding of the settings glyphs
 *  so the two surfaces share an aesthetic. */
function ChannelRail({
  brandSlug,
  kind,
  active,
}: {
  brandSlug: string;
  kind: BrandChannelKind;
  active: ChannelSection;
}) {
  const { t } = useLingui();
  // The `slug`s are the section search-param values — identifiers, not
  // copy. Only label + subtitle are read by a human.
  const items: Array<{ slug: ChannelSection; label: string; subtitle: string }> = [
    { slug: 'control', label: t`control`, subtitle: t`how i run this channel.` },
    { slug: 'plan', label: t`content plan`, subtitle: t`cadence + pillars.` },
    { slug: 'slots', label: t`slots`, subtitle: t`what i'm shipping next.` },
  ];
  return (
    <aside>
      <nav className="flex flex-col gap-1 md:sticky md:top-20">
        {items.map((item) => (
          <ChannelRailLink
            key={item.slug}
            slug={item.slug}
            label={item.label}
            subtitle={item.subtitle}
            brandSlug={brandSlug}
            kind={kind}
            isActive={item.slug === active}
          />
        ))}
      </nav>
    </aside>
  );
}

function ChannelRailLink({
  slug,
  label,
  subtitle,
  brandSlug,
  kind,
  isActive,
}: {
  slug: ChannelSection;
  label: string;
  subtitle: string;
  brandSlug: string;
  kind: BrandChannelKind;
  isActive: boolean;
}) {
  const inactive = [
    'group flex items-center gap-2.5 rounded-md',
    'border border-transparent px-2.5 py-2',
    'no-underline transition-all duration-150',
    'hover:border-[var(--line)] hover:bg-white',
  ].join(' ');
  const active = [
    'group flex items-center gap-2.5 rounded-md',
    'border-[1.5px] border-[var(--ink)] bg-[var(--spell)] px-2.5 py-2',
    'no-underline shadow-[2px_2px_0_var(--plum)] transition-all duration-150',
  ].join(' ');
  return (
    <Link
      to="/brand/$slug/channels/$kind"
      params={{ slug: brandSlug, kind }}
      search={{ section: slug }}
      className={isActive ? active : inactive}
    >
      <ChannelRailGlyph slug={slug} isActive={isActive} />
      <span className="min-w-0 flex-1">
        <span className="block font-display text-[13px] font-semibold leading-tight text-[var(--ink)]">
          {label}
        </span>
        <span className="mt-0.5 block text-[11px] leading-snug text-[var(--ink)]/55">
          {subtitle}
        </span>
      </span>
      <span
        aria-hidden="true"
        className={`font-mono text-[12px] ${isActive ? 'text-[var(--ink)]/70' : 'text-[var(--ink)]/0'}`}
      >
        →
      </span>
    </Link>
  );
}

function ChannelRailGlyph({ slug, isActive }: { slug: ChannelSection; isActive: boolean }) {
  const { t } = useLingui();
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 16 16',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.4,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': 'true' as const,
    className: `shrink-0 ${isActive ? 'text-[var(--ink)]' : 'text-[var(--ink)]/65'}`,
  };
  switch (slug) {
    case 'control':
      // Lever / toggle — control is about flipping switches.
      return (
        <svg {...common}>
          <title>{t`control`}</title>
          <circle cx="4.5" cy="6" r="1.5" />
          <circle cx="11.5" cy="11" r="1.5" />
          <path d="M2 6h1M6 6h8M2 11h8M13 11h1" />
        </svg>
      );
    case 'plan':
      // Stacked lines — a content plan is a structured rhythm.
      return (
        <svg {...common}>
          <title>{t`content plan`}</title>
          <path d="M3 4h10M3 8h10M3 12h7" />
        </svg>
      );
    case 'slots':
      // Calendar grid — slots are dated posts on a schedule.
      return (
        <svg {...common}>
          <title>{t`slots`}</title>
          <rect x="2.5" y="3.5" width="11" height="10" rx="1.5" />
          <path d="M2.5 7h11M6 2.5v2M10 2.5v2" />
        </svg>
      );
    default:
      return null;
  }
}

function BackLink({ brand }: { brand: Brand }) {
  return (
    <Link
      to="/brand/$slug/channels"
      params={{ slug: brand.slug }}
      className="mb-4 inline-flex items-center gap-1 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55 underline-offset-2 hover:text-[var(--ink)] hover:underline"
    >
      {'←'} <Trans>all channels</Trans>
    </Link>
  );
}

function Header({
  brand: _brand,
  kind,
  channel,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  channel: BrandChannelDTO | null;
}) {
  return (
    <header className="mb-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>channel</Trans> · {kind}
      </p>
      <h1 className="mt-1 font-display text-[28px] font-bold leading-tight tracking-[-0.02em] text-[var(--ink)]">
        {labelForKind(kind)}.
      </h1>
      {channel ? <ChannelStatusLine channel={channel} /> : null}
    </header>
  );
}

function ChannelStatusLine({ channel }: { channel: BrandChannelDTO }) {
  if (channel.status === 'archived') {
    return (
      <p className="mt-2 text-[13px] text-[var(--ink)]/55">
        <Trans>archived. historical posts stay in the timeline; no new drafting.</Trans>
      </p>
    );
  }
  if (channel.status === 'pending_connection') {
    // pending_connection covers two states: a fresh OAuth handshake
    // that hasn't landed yet, AND a post-disconnect row preserving
    // intake config for an eventual reconnect. The honest line for
    // either reads as "not live"; the body of the page (NotConnected)
    // surfaces the reconnect CTA.
    return (
      <p className="mt-2 text-[13px] text-[var(--ink)]/65">
        <Trans>not connected. reconnect to let me start drafting again.</Trans>
      </p>
    );
  }
  const paused = channel.pausedUntil && new Date(channel.pausedUntil).getTime() > Date.now();
  if (paused) {
    return (
      <p className="mt-2 text-[13px] text-[var(--ink)]/65">
        {'↘'} <Trans>paused until {formatDate(channel.pausedUntil)}.</Trans>
      </p>
    );
  }
  if (!channel.operatedByMag) {
    return (
      <p className="mt-2 text-[13px] text-[var(--ink)]/65">
        <Trans>you're operating this one — i'm not drafting or publishing.</Trans>
      </p>
    );
  }
  return (
    <p className="mt-2 text-[13px] text-[var(--ink)]/65">
      <Trans>i'm running it on cadence. plan + upcoming slots below.</Trans>
    </p>
  );
}

function PlanSection({
  brand,
  kind,
  plan,
  channel,
  proposal,
  slots,
  retryAt,
  onRetry,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  plan: BrandContentPlanDTO | null;
  channel: BrandChannelDTO;
  proposal: ChannelTakeoverProposalEnvelope | null;
  slots: BrandContentSlotDTO[];
  retryAt: number | null;
  onRetry: () => void;
}) {
  if (!plan) {
    // [s263] When the founder has NOT handed the channel over yet,
    // mag isn't drafting — the api's meta-finalize handler upserts
    // operatedByMag=false on connect and does NOT fire the
    // orchestrator. Showing the drafting loader anyway (because the
    // connection is "young") directly contradicts the header's
    // "you're operating this one — i'm not drafting or publishing."
    // line, which is what triggered this fix. Drop straight to the
    // "no plan yet" message and let the ProposalSection above carry
    // the "let me run this" CTA.
    if (!channel.operatedByMag) {
      return (
        <section className="mb-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
            {'↘'} <Trans>content plan</Trans>
          </p>
          <p className="mt-2 text-[13px] text-[var(--ink)]/65">
            <Trans>no plan yet. i'll draft one once you tell me to run this channel.</Trans>
          </p>
        </section>
      );
    }
    // operatedByMag === true — mag has been told to run it. Three
    // paths to the "drafting" state from here:
    //   1. Channel just connected AND was handed over at the same
    //      time — the hand-over endpoint fires the orchestrator
    //      (history-learn → content-plan).
    //   2. Founder JUST approved mag's takeover proposal — the
    //      approve endpoint re-fires the orchestrator.
    //   3. Founder JUST clicked retry from this card — the
    //      hand-over endpoint re-fires the orchestrator. Local-only
    //      timestamp (retryAt), no server side-effect needed.
    // Anchor the spinner on whichever age is smallest.
    const now = Date.now();
    const connectedAtAge = channel.connectedAt
      ? now - new Date(channel.connectedAt).getTime()
      : null;
    const decidedAtAge =
      proposal?.status === 'approved' && proposal.decidedAt
        ? now - new Date(proposal.decidedAt).getTime()
        : null;
    const retryAge = retryAt !== null ? now - retryAt : null;
    const inOnboardingWindow = connectedAtAge !== null && connectedAtAge < ONBOARDING_WINDOW_MS;
    const inPostApprovalWindow = decidedAtAge !== null && decidedAtAge < POST_APPROVAL_WINDOW_MS;
    const inRetryWindow = retryAge !== null && retryAge < POST_APPROVAL_WINDOW_MS;
    if (inOnboardingWindow || inPostApprovalWindow || inRetryWindow) {
      const candidates = [
        inRetryWindow ? retryAge : null,
        inPostApprovalWindow ? decidedAtAge : null,
        inOnboardingWindow ? connectedAtAge : null,
      ].filter((v): v is number => v !== null);
      const ageMs = candidates.length > 0 ? Math.min(...candidates) : 0;
      return <PlanOnboardingState ageMs={ageMs} />;
    }
    // operatedByMag is on but no plan landed + no active drafting
    // window → orchestrator failed silently. Surface a retry CTA.
    return <PlanDraftingFailedRetry brand={brand} kind={kind} onRetryFired={onRetry} />;
  }
  const upcomingCount = slots.filter((s) => !s.publishedAt).length;
  return (
    <section className="mb-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
          {'↘'} <Trans>content plan</Trans>
        </p>
        <RedrawPlanControl
          brand={brand}
          kind={kind}
          upcomingCount={upcomingCount}
          onRedrawFired={onRetry}
        />
      </div>
      {/* summary, pillar labels and pillar descriptions are all
          written by mag when she drafts the plan — stamped prose
          (wave 5), not interface chrome. */}
      {plan.summary ? (
        <p className="mt-2 text-[13px] text-[var(--ink)]/85">{plan.summary}</p>
      ) : null}
      <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        <Trans>
          cadence: {plan.cadence.postsPerWeek}/week · tz {plan.cadence.tz}
        </Trans>
      </p>
      <div className="mt-3">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
          {'↘'} <Trans>pillars</Trans>
        </p>
        <ul className="mt-1.5 flex flex-col gap-1.5">
          {plan.pillars.map((p) => (
            <li
              key={p.label}
              className="rounded-xl border border-[var(--ink)]/10 bg-[var(--paper)] p-2.5"
            >
              <p className="flex items-baseline justify-between gap-2 text-[13px] font-semibold text-[var(--ink)]">
                {p.label.toLowerCase()}
                <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                  {p.weightPercent}%
                </span>
              </p>
              <p className="mt-0.5 text-[12.5px] text-[var(--ink)]/65">{p.description}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/** Two-step "redraw plan" affordance for the populated plan card.
 *  Step 1 is a quiet ghost link (so it doesn't compete with publish);
 *  step 2 is an inline mag-voice confirmation card with the count of
 *  unpublished slots that will be wiped + the actual fire button. */
function RedrawPlanControl({
  brand,
  kind,
  upcomingCount,
  onRedrawFired,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  upcomingCount: number;
  onRedrawFired: () => void;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const mutation = useMutation({
    mutationFn: async () => rebuildBrandChannelPlan(brand.id, kind),
    onSuccess: async (result) => {
      onRedrawFired();
      setConfirming(false);
      await refreshQueries(queryClient, ['brand-channel', brand.id, kind]);
      toast.success(
        result.deletedSlots > 0
          ? t`cleared ${result.deletedSlots} upcoming slots — i'm drafting a fresh plan.`
          : t`drafting a fresh plan now — i'll have it ready in a minute.`,
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55 transition hover:text-[var(--plum)]"
      >
        {'↻'} <Trans>redraw</Trans>
      </button>
    );
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <p className="mag-voice max-w-[280px] text-right text-[12px] leading-[1.45] text-[var(--ink)]/75">
        {upcomingCount > 0
          ? plural(upcomingCount, {
              one: "“i'll wipe the # upcoming slot that hasn't shipped and draft a fresh plan. published posts stay put.”",
              other:
                "“i'll wipe the # upcoming slots that haven't shipped and draft a fresh plan. published posts stay put.”",
            })
          : t`“i'll draft a fresh plan from scratch — nothing to wipe, no posts are pending.”`}
      </p>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setConfirming(false)}
          disabled={mutation.isPending}
          className="btn btn-sm btn-ghost disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trans>cancel</Trans>
        </button>
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="btn btn-sm btn-spell disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? t`redrawing…` : t`redraw plan`}
          {!mutation.isPending ? <span className="btn-arrow">→</span> : null}
        </button>
      </div>
    </div>
  );
}

// Active "↘ reading your account" state shown on the channel detail
// page while the channel-onboarding orchestrator (history-learn →
// content-plan) is running. The page polls every 3s so this card
// auto-flips to the real PlanSection the moment the plan lands.
function PlanOnboardingState({ ageMs }: { ageMs: number }) {
  const { t } = useLingui();
  // Approximate copy stages based on elapsed time since connection.
  // We don't have ground-truth per-step progress (orchestrator fires
  // fire-and-forget without a progress table), so these are educated
  // guesses based on the typical history-learn + plan-draft durations
  // (~30-90s + ~20-30s respectively).
  const ageSec = Math.floor(ageMs / 1000);
  let label: string;
  let receipt: string;
  if (ageSec < 30) {
    label = t`reading your instagram account`;
    receipt = t`i'm going through your last few months of posts. give me a sec.`;
  } else if (ageSec < 90) {
    label = t`reading what you've already shipped`;
    receipt = t`picking up your voice patterns + what cadence your audience has earned.`;
  } else if (ageSec < 180) {
    label = t`drafting your plan`;
    receipt = t`writing up the cadence, pillars, and the next slots.`;
  } else {
    label = t`almost there`;
    receipt = t`wrapping things up — should land any second now.`;
  }

  return (
    <section className="mb-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        ↘ {label}
      </p>
      <div className="mt-3 flex items-start gap-3">
        <div className="relative shrink-0">
          <div
            aria-hidden="true"
            className="mag-aura pointer-events-none absolute inset-[-14px] rounded-full"
          />
          <div className="mag-breathe relative">
            <Mag size={56} mood="casting" />
          </div>
        </div>
        <div className="flex-1">
          <p className="font-serif text-[14px] leading-[1.5] text-[var(--ink)]/85">{receipt}</p>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            {'↘'} <Trans>this page will update on its own as mag finishes.</Trans>
          </p>
        </div>
      </div>
    </section>
  );
}

/** Shown when mag is operating the channel but no plan has landed
 *  and every drafting window has expired. The orchestrator either
 *  failed silently, hit a quota, or the founder dismissed the page
 *  before it finished. Surfaces a one-click retry that re-fires the
 *  hand-over endpoint (idempotent — calls runChannelOnboarding again
 *  even though operatedByMag is already true). The parent's retryAt
 *  flips on success which restarts polling. */
function PlanDraftingFailedRetry({
  brand,
  kind,
  onRetryFired,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  onRetryFired: () => void;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const retryMutation = useMutation({
    mutationFn: async () => handOverBrandChannel(brand.id, kind),
    onSuccess: async () => {
      onRetryFired();
      await refreshQueries(queryClient, ['brand-channel', brand.id, kind]);
      toast.success(t`re-firing — i'll have a plan ready in a minute.`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  return (
    <section className="mb-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>content plan</Trans>
      </p>
      <div className="mt-3 flex items-start gap-3">
        <div className="relative shrink-0">
          <Mag size={48} mood="confused" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="mag-voice text-[14px] leading-[1.5] text-[var(--ink)]/85">
            <Trans>
              “i tried to draft your plan and it didn't land. probably a quota or a flaky connection
              on my side — not anything you did. let me retry.”
            </Trans>
          </p>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => retryMutation.mutate()}
          disabled={retryMutation.isPending}
          className="btn btn-spell disabled:cursor-not-allowed disabled:opacity-50"
        >
          {retryMutation.isPending ? t`re-firing…` : t`retry drafting`}
          {!retryMutation.isPending ? <span className="btn-arrow">→</span> : null}
        </button>
      </div>
    </section>
  );
}

function SlotsSection({
  slots,
  brand,
  kind,
  channel,
  initialOpenSlotId,
}: {
  slots: BrandContentSlotDTO[];
  brand: Brand;
  kind: BrandChannelKind;
  channel: BrandChannelDTO;
  /** s216-54h.61 — when set on initial mount, pre-opens the slot
   *  drawer for that id. The calendar chip click uses ?slot=<id> in
   *  the URL to land the founder directly on the right draft. */
  initialOpenSlotId: string | null;
}) {
  const navigate = useNavigate();
  // Slots arrive ascending by scheduledFor (listSlotsForChannel), so the
  // first unpublished one is the very next post going out. Pull it out as
  // a featured "next up" hero so the founder always knows what's about to
  // ship + whether mag is waiting on them — the rest fall into the list.
  const upcoming = slots.filter((s) => !s.publishedAt);
  const nextUp = upcoming[0] ?? null;
  const restUpcoming = upcoming.slice(1);
  const published = slots.filter((s) => s.publishedAt).slice(0, 5);
  const [openSlotId, setOpenSlotId] = useState<string | null>(initialOpenSlotId);
  // When the URL's ?slot changes after mount (e.g. founder clicks
  // another day's chip without reloading), follow it. Initial mount
  // already seeds from initialOpenSlotId so this only fires on
  // subsequent navigations.
  useEffect(() => {
    setOpenSlotId(initialOpenSlotId);
  }, [initialOpenSlotId]);
  const handleClose = () => {
    setOpenSlotId(null);
    // Drop ?slot=<id> from the URL so a reload doesn't re-open the
    // drawer the founder just dismissed. Keep section=slots so the
    // founder lands back on the same surface, not the default tab.
    void navigate({
      to: '/brand/$slug/channels/$kind',
      params: { slug: brand.slug, kind },
      search: { section: 'slots' as const },
      replace: true,
    });
  };
  const openSlot = openSlotId ? (slots.find((s) => s.id === openSlotId) ?? null) : null;
  return (
    <section className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} <Trans>slots</Trans>
      </p>
      {upcoming.length === 0 && published.length === 0 ? (
        <p className="mt-2 text-[13px] text-[var(--ink)]/65">
          <Trans>no slots yet. they'll appear here once i've drafted the cadence.</Trans>
        </p>
      ) : null}
      {nextUp ? (
        <div className="mt-3">
          <NextUpCard slot={nextUp} onOpen={() => setOpenSlotId(nextUp.id)} />
        </div>
      ) : null}
      {restUpcoming.length > 0 ? (
        <div className="mt-4">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
            {'↘'} <Trans>then</Trans>
          </p>
          <ul className="mt-1.5 flex flex-col gap-2">
            {restUpcoming.slice(0, 12).map((s) => (
              <SlotRow key={s.id} slot={s} onOpen={() => setOpenSlotId(s.id)} />
            ))}
          </ul>
        </div>
      ) : null}
      {published.length > 0 ? (
        <div className="mt-4">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
            {'↘'} <Trans>recently published</Trans>
          </p>
          <ul className="mt-1.5 flex flex-col gap-2">
            {published.map((s) => (
              <SlotRow key={s.id} slot={s} onOpen={() => setOpenSlotId(s.id)} />
            ))}
          </ul>
        </div>
      ) : null}
      {openSlot ? (
        <SlotDrawer
          slot={openSlot}
          brand={brand}
          kind={kind}
          channel={channel}
          onClose={handleClose}
        />
      ) : null}
    </section>
  );
}

/** Featured hero for the very next post going out. Larger + louder than
 *  a SlotRow: the founder lands on the slots tab to know two things —
 *  what's about to ship, and whether mag is waiting on them. When the
 *  slot is `final_pending` (draft ready, founder must approve before it
 *  ships) the whole card flips to a plum "waiting on you" treatment with
 *  a review CTA; otherwise it narrates mag's progress in her voice. */
function NextUpCard({ slot, onOpen }: { slot: BrandContentSlotDTO; onOpen: () => void }) {
  const { t } = useLingui();
  const when = formatRelativeWhen(t, slot.scheduledFor);
  // final_pending is the one approval state that's the FOUNDER's move —
  // the draft is done and nothing ships until they approve (it's what
  // fires the slot_approval_pending card). Other states (concept_only,
  // auto_prepping, awaiting_designer) are mag's / the designer's turn.
  const needsYou = slot.approvalStatus === 'final_pending';
  const isVideoSlot = slot.format === 'reel' || slot.format === 'video';
  const hasDraft = slot.draft !== null;
  const hasMedia = hasDraft
    ? isVideoSlot
      ? Boolean(slot.draft?.videoUrl)
      : Boolean(slot.draft?.imageUrl)
    : false;
  const preview = slot.draft?.caption?.split('\n').find((s) => s.trim().length > 0) ?? slot.concept;
  const statusLine = needsYou
    ? t`i've drafted it — give it a look and approve so i can ship it.`
    : hasDraft && hasMedia
      ? t`ready to go — i'll ship it at the scheduled time.`
      : hasDraft
        ? t`copy's done — i'm rendering the visual now.`
        : t`i'm drafting this now — i'll ping you the moment it's ready to look at.`;

  return (
    <button
      type="button"
      onClick={onOpen}
      className={`group flex w-full items-stretch gap-4 rounded-2xl border-[1.5px] p-3.5 text-left transition ${
        needsYou
          ? 'border-[var(--plum)] bg-[var(--spell-soft)]/60 shadow-[3px_3px_0_var(--plum)] hover:shadow-[4px_4px_0_var(--plum)]'
          : 'border-[var(--ink)] bg-[var(--paper)] shadow-[3px_3px_0_var(--spell)] hover:shadow-[4px_4px_0_var(--spell)]'
      }`}
    >
      <SlotThumb slot={slot} />
      <div className="flex min-w-0 flex-1 flex-col justify-between gap-2 py-0.5">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px] font-semibold uppercase tracking-[0.1em]">
            <span className="text-[var(--plum)]">
              {'↘'} <Trans>next up</Trans>
            </span>
            <span aria-hidden="true" className="text-[var(--ink)]/30">
              ·
            </span>
            <span className="text-[var(--ink)]/70">{when}</span>
            <span aria-hidden="true" className="text-[var(--ink)]/30">
              ·
            </span>
            <span className="text-[var(--ink)]/55">{slot.format}</span>
            {needsYou ? (
              <span className="rounded-full border border-[var(--plum)] bg-[var(--plum)] px-2 py-0.5 text-[9px] tracking-[0.08em] text-[var(--cream)]">
                <Trans>waiting on you</Trans>
              </span>
            ) : null}
          </p>
          <p className="mt-1.5 line-clamp-2 font-display text-[15px] font-bold leading-[1.3] tracking-[-0.01em] text-[var(--ink)]">
            {preview}
          </p>
        </div>
        <p
          className={`mag-voice text-[12.5px] leading-[1.4] ${
            needsYou ? 'text-[var(--ink)]' : 'text-[var(--ink)]/65'
          }`}
        >
          “{statusLine}”
        </p>
      </div>
      <span className="self-center whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/45 transition group-hover:text-[var(--ink)]">
        {needsYou ? t`review →` : t`open →`}
      </span>
    </button>
  );
}

function SlotRow({ slot, onOpen }: { slot: BrandContentSlotDTO; onOpen: () => void }) {
  const { t } = useLingui();
  const when = formatDateTime(slot.scheduledFor);
  const isVideoSlot = slot.format === 'reel' || slot.format === 'video';
  const hasDraft = slot.draft !== null;
  const hasMedia = hasDraft
    ? isVideoSlot
      ? Boolean(slot.draft?.videoUrl)
      : Boolean(slot.draft?.imageUrl)
    : false;
  const isPublished = Boolean(slot.publishedAt);
  // Surface the founder's-move state in the list too, so an approval-
  // waiting slot is visible even when it isn't the next one up.
  const needsYou = slot.approvalStatus === 'final_pending';
  const isReady = !isPublished && !needsYou && hasDraft && hasMedia;
  const status = isPublished
    ? t`shipped`
    : needsYou
      ? t`waiting on you`
      : isReady
        ? t`ready`
        : hasDraft
          ? t`needs media`
          : t`awaiting draft`;
  const statusTone = isPublished
    ? 'text-[var(--ink)]/45'
    : needsYou || isReady
      ? 'text-[var(--plum)]'
      : 'text-[var(--ink)]/55';
  // First line of the caption when drafted; falls back to concept.
  const preview = slot.draft?.caption?.split('\n').find((s) => s.trim().length > 0) ?? slot.concept;

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full items-stretch gap-3 rounded-xl border border-[var(--ink)]/10 bg-[var(--paper)] p-2 text-left transition hover:border-[var(--ink)]/25 hover:shadow-[2px_2px_0_var(--spell)]"
      >
        <SlotThumb slot={slot} />
        <div className="flex min-w-0 flex-1 flex-col justify-between gap-1.5 py-0.5 pr-1">
          <div className="min-w-0">
            <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
              <span>{when}</span>
              <span aria-hidden="true">·</span>
              <span>{slot.format}</span>
            </p>
            <p className="mt-1 line-clamp-2 text-[13px] leading-[1.4] text-[var(--ink)]/85">
              {preview}
            </p>
          </div>
          <p
            className={`font-mono text-[9.5px] uppercase tracking-[0.1em] ${statusTone} transition group-hover:text-[var(--ink)]`}
          >
            {'↘'} {status} · <Trans>open →</Trans>
          </p>
        </div>
      </button>
    </li>
  );
}

/** Square thumb on the left of a slot row. Image / video frame when
 *  available; pillar-tinted placeholder otherwise so the row keeps a
 *  consistent visual anchor whether or not media has rendered yet. */
function SlotThumb({ slot }: { slot: BrandContentSlotDTO }) {
  const isVideoSlot = slot.format === 'reel' || slot.format === 'video';
  const imageUrl = slot.draft?.imageUrl;
  const videoUrl = slot.draft?.videoUrl;
  if (!isVideoSlot && imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        aria-hidden="true"
        className="h-16 w-16 shrink-0 rounded-lg border border-[var(--ink)]/15 object-cover"
      />
    );
  }
  if (isVideoSlot && videoUrl) {
    return (
      <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-[var(--ink)]/15 bg-black">
        <video
          src={videoUrl}
          muted
          playsInline
          preload="metadata"
          className="block h-full w-full object-cover"
        >
          <track kind="captions" />
        </video>
        <span
          aria-hidden="true"
          className="absolute inset-0 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.1em] text-white/90"
        >
          ▶
        </span>
      </div>
    );
  }
  return (
    <div
      aria-hidden="true"
      className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-dashed border-[var(--ink)]/20 bg-[var(--cream)] font-mono text-[9px] uppercase tracking-[0.1em] text-[var(--ink)]/40"
    >
      {isVideoSlot ? <Trans>reel</Trans> : <Trans>post</Trans>}
    </div>
  );
}

function ControlSection({
  brand,
  kind,
  channel,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  channel: BrandChannelDTO;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [pauseDays, setPauseDays] = useState(7);

  const refreshChannel = async () => {
    await refreshQueries(
      queryClient,
      ['brand-channel', brand.id, kind],
      ['brand-channels', brand.id],
    );
  };

  const pauseMutation = useMutation({
    mutationFn: async () => pauseBrandChannel(brand.id, kind, pauseDays),
    onSuccess: async () => {
      await refreshChannel();
      toast.success(plural(pauseDays, { one: 'paused for # day', other: 'paused for # days' }));
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const unpauseMutation = useMutation({
    mutationFn: async () => unpauseBrandChannel(brand.id, kind),
    onSuccess: async () => {
      await refreshChannel();
      toast.success(t`paused window cleared`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // [12.2L-173] Two distinct mutations now, intentionally split:
  //   - handBack: founder takes operating control. Just flips
  //     operatedByMag=false; no orchestration needed because nothing
  //     is starting up. Uses the existing operated-by-mag endpoint.
  //   - handOver: founder hands operating control to mag. Hits the
  //     new /hand-over endpoint which flips operatedByMag=true AND
  //     fires runChannelOnboarding (history-learn + content-plan).
  //     The split makes the consent moment explicit — granting Meta
  //     OAuth scopes no longer implies "mag is publishing for me."
  const handBackMutation = useMutation({
    mutationFn: async () => setBrandChannelOperatedByMag(brand.id, kind, false),
    onSuccess: async () => {
      await refreshChannel();
      toast.success(t`you're driving — i'll stay back`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const handOverMutation = useMutation({
    mutationFn: async () => handOverBrandChannel(brand.id, kind),
    onSuccess: async () => {
      await refreshChannel();
      toast.success(t`i've got this one — drafting a content plan now`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const operatedMutationPending = handBackMutation.isPending || handOverMutation.isPending;

  const paused = channel.pausedUntil && new Date(channel.pausedUntil).getTime() > Date.now();

  return (
    <section className="mb-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--ink)]/55">
        {'↘'} <Trans>control</Trans>
      </p>

      <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-[var(--ink)]">
            <Trans>mag operates this channel</Trans>
          </p>
          <p className="mt-0.5 text-[12.5px] text-[var(--ink)]/65">
            {channel.operatedByMag
              ? t`i'm drafting + publishing on cadence.`
              : t`you're driving. i won't draft or publish here.`}
          </p>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            disabled={operatedMutationPending}
            onClick={() => {
              if (channel.operatedByMag) {
                handBackMutation.mutate();
              } else {
                handOverMutation.mutate();
              }
            }}
            className={`btn ${channel.operatedByMag ? 'btn-ghost' : 'btn-spell'} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {operatedMutationPending
              ? channel.operatedByMag
                ? t`handing back…`
                : t`starting up…`
              : channel.operatedByMag
                ? t`hand it back to me`
                : t`let mag run this`}
            {!operatedMutationPending ? <span className="btn-arrow">→</span> : null}
          </button>
        </div>
      </div>

      <hr className="my-4 border-t border-[var(--ink)]/10" />

      <ChannelAutopilotRow brand={brand} kind={kind} channel={channel} />

      <hr className="my-4 border-t border-[var(--ink)]/10" />

      <ChannelLeadTimeRow brand={brand} kind={kind} channel={channel} />

      <hr className="my-4 border-t border-[var(--ink)]/10" />

      <ChannelCreativeModeRow brand={brand} kind={kind} channel={channel} />

      {channel.creativeMode === 'designer' ? (
        <>
          <hr className="my-4 border-t border-[var(--ink)]/10" />
          <ChannelDesignerRouteRow brand={brand} kind={kind} channel={channel} />
        </>
      ) : null}

      <hr className="my-4 border-t border-[var(--ink)]/10" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-[var(--ink)]">
            {paused ? t`paused` : t`pause posting`}
          </p>
          <p className="mt-0.5 text-[12.5px] text-[var(--ink)]/65">
            {paused
              ? t`cleared after ${formatDate(channel.pausedUntil)}. unpause to resume earlier.`
              : t`freezes drafting + publishing for a window. drafts already queued stay drafts.`}
          </p>
        </div>
        <div className="flex flex-col items-end gap-3 sm:flex-row sm:items-end">
          {!paused ? (
            <label className="flex items-center gap-2 font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
              <Trans>days</Trans>
              <input
                type="number"
                min={1}
                max={60}
                value={pauseDays}
                onChange={(e) => setPauseDays(Math.max(1, Math.min(60, Number(e.target.value))))}
                className="field-input w-16 text-right font-mono text-[14px] font-semibold text-[var(--ink)]"
              />
            </label>
          ) : null}
          <button
            type="button"
            disabled={pauseMutation.isPending || unpauseMutation.isPending}
            onClick={() => (paused ? unpauseMutation.mutate() : pauseMutation.mutate())}
            className="btn btn-ghost disabled:cursor-not-allowed disabled:opacity-50"
          >
            {paused ? t`unpause` : t`pause ${pauseDays}d`}
            {!pauseMutation.isPending && !unpauseMutation.isPending ? (
              <span className="btn-arrow">↘</span>
            ) : null}
          </button>
        </div>
      </div>

      <hr className="my-4 border-t border-[var(--ink)]/10" />

      <ChannelDisconnectRow brand={brand} kind={kind} channel={channel} />
    </section>
  );
}

/** [channel-detail-disconnect] Disconnect row at the bottom of every
 *  channel's CONTROL section. For instagram, "disconnect" revokes the
 *  meta OAuth credential (kills posting + reading); for other channel
 *  kinds the row stays present but renders a stub since the connect
 *  adapter doesn't ship yet — the universal affordance signals "this
 *  is how you reset a channel" without the founder having to hunt
 *  through settings for it. The actual brand_channels row stays put
 *  so paused / authority / creative-mode settings survive a reconnect;
 *  meta-finalize on the next OAuth round-trip reuses the row. */
function ChannelDisconnectRow({
  brand,
  kind,
  channel: _channel,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  channel: BrandChannelDTO;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isInstagram = kind === 'instagram';
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Founder's choice: clear the queued plan on disconnect, or keep it
  // for a reconnect. Default keep (false) — non-destructive.
  const [deleteSlots, setDeleteSlots] = useState(false);
  const disconnectMutation = useMutation({
    mutationFn: async () => disconnectMeta(brand.id, { deleteSlots }),
    onSuccess: async ({ slotsDeleted }) => {
      await refreshQueries(
        queryClient,
        ['brand-channel', brand.id, kind],
        ['brand-channels', brand.id],
        ['meta', 'connection', brand.id],
        ['feed'],
        // Disconnect drops the channel out of the calendar (slots for a
        // pending_connection channel are no longer surfaced) — refetch so
        // the editorial calendar + home "next 14 days" widget update now.
        ['brand-calendar', brand.id],
      );
      toast.success(
        slotsDeleted > 0
          ? plural(slotsDeleted, {
              one: 'disconnected. cleared # queued draft.',
              other: 'disconnected. cleared # queued drafts.',
            })
          : t`disconnected. i won't touch this account again until you reconnect.`,
      );
      // Land on the channels index — clean reset surface where the
      // founder picks a channel to reconnect from. Saves them having
      // to navigate manually after the page no longer has a live
      // connection backing it.
      await navigate({ to: '/brand/$slug/channels', params: { slug: brand.slug } });
    },
    onError: (err: Error) => toast.error(err.message),
  });
  return (
    <>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-[var(--ink)]">
            <Trans>disconnect {kind}</Trans>
          </p>
          <p className="mt-0.5 text-[12.5px] text-[var(--ink)]/65">
            {isInstagram
              ? t`revokes meta access for this brand. drafts already queued stay drafts but nothing new posts until you reconnect.`
              : t`connect adapter for ${kind} ships later. when it does, this is where you'll unwire it.`}
          </p>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            disabled={!isInstagram || disconnectMutation.isPending}
            onClick={() => isInstagram && setConfirmOpen(true)}
            className={`btn ${isInstagram ? 'btn-pulse' : 'btn-ghost'} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {disconnectMutation.isPending ? t`disconnecting…` : t`disconnect`}
            {!disconnectMutation.isPending && isInstagram ? (
              <span className="btn-arrow">→</span>
            ) : null}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={t`disconnect ${kind}?`}
        body={
          <>
            <Trans>
              i'll stop drafting + publishing here, and <b>meta paid ads on the same connection</b>{' '}
              will also stop serving until you reconnect. settings on this channel (cadence,
              authority, designer) stay put so a reconnect picks up where you left off.
            </Trans>
            <DeleteSlotsChoice checked={deleteSlots} onChange={setDeleteSlots} />
          </>
        }
        confirmLabel={t`disconnect`}
        busyLabel={t`disconnecting…`}
        destructive
        busy={disconnectMutation.isPending}
        onConfirm={() => disconnectMutation.mutate()}
        onCancel={() => {
          setDeleteSlots(false);
          setConfirmOpen(false);
        }}
      />
    </>
  );
}

function NotConnected({ kind, brand }: { kind: BrandChannelKind; brand: Brand }) {
  const { t } = useLingui();
  // Instagram is the only channel with a real connect adapter today —
  // every other kind ('linkedin', 'tiktok', 'youtube') only exists as
  // a "coming soon" tile on the channels list and shouldn't be
  // reachable here, but if the founder hand-edits the URL we keep
  // the fallback copy honest.
  const supportsConnect = kind === 'instagram';
  const onConnect = () => {
    // Instagram is the meta_organic bundle. paid is a separate scope
    // that the founder grants from /settings/integrations (or the
    // future "run paid ads" channel). starting with organic only
    // means a smaller permission ask in the meta dialog — easier
    // approval, less friction.
    // [channel-intake] Land the founder INSIDE the chat-with-mag
    // drawer once OAuth completes — not back on this NotConnected
    // shell. parseReturnPath allows path + query string, so
    // ?intake=open survives the round-trip and the channel detail
    // page auto-mounts the drawer.
    const returnTo = `/brand/${brand.slug}/channels/${kind}?intake=open`;
    window.location.assign(metaConnectStartUrl(brand.id, { bundles: ['meta_organic'], returnTo }));
  };
  return (
    <div className="rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-6 shadow-[4px_4px_0_var(--spell)]">
      <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
        {'↘'} <Trans>not connected</Trans>
      </p>
      <h3 className="mt-1 font-display text-[16px] font-bold tracking-[-0.01em] text-[var(--ink)]">
        <Trans>connect {kind} to let me run it.</Trans>
      </h3>
      <p className="mt-2 mag-voice text-[14px] leading-[1.45] text-[var(--ink)]/70">
        {supportsConnect
          ? t`“i'll post on a cadence that fits your voice. you can pause me anytime.”`
          : t`“head to settings · integrations to wire this one up.”`}
      </p>
      {supportsConnect ? (
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={onConnect} className="btn">
            <Trans>connect {kind}</Trans>
            <span className="btn-arrow">→</span>
          </button>
        </div>
      ) : null}
    </div>
  );
}

function labelForKind(kind: BrandChannelKind): string {
  switch (kind) {
    case 'instagram':
      return 'instagram';
    case 'linkedin':
      return 'linkedin';
    case 'tiktok':
      return 'tiktok';
    case 'youtube':
      return 'youtube';
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return formatDateIntl(iso, { month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    const d = new Date(iso);
    const day = formatDateIntl(d, { month: 'short', day: 'numeric' });
    const hour = formatTime(d, { hour: 'numeric', minute: '2-digit' });
    return `${day} ${hour}`;
  } catch {
    return iso;
  }
}

/** Friendlier "today · 9:00 pm" / "tomorrow · 9:00 pm" / "Sat, Jun 14 ·
 *  9:00 pm" for the next-up hero, so the founder reads the timing at a
 *  glance instead of decoding a bare date. Diff is in calendar days
 *  (local), so a slot late tonight still reads "today". */
type T = ReturnType<typeof useLingui>['t'];

function formatRelativeWhen(t: T, iso: string): string {
  try {
    const d = new Date(iso);
    const hour = formatTime(d, { hour: 'numeric', minute: '2-digit' });
    const startToday = new Date();
    startToday.setHours(0, 0, 0, 0);
    const startSlot = new Date(d);
    startSlot.setHours(0, 0, 0, 0);
    const diffDays = Math.round((startSlot.getTime() - startToday.getTime()) / 86_400_000);
    if (diffDays === 0) return i18n._(msg`today · ${hour}`);
    if (diffDays === 1) return i18n._(msg`tomorrow · ${hour}`);
    const day = formatDateIntl(d, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    return `${day} · ${hour}`;
  } catch {
    return iso;
  }
}

// ─── Channel takeover proposal section ──────────────────────────────
//
// Only renders when the channel is connected but operatedByMag=false.
// Three states it walks through:
//   1. No proposal yet (or last one was discarded) → "ask mag for a
//      plan" CTA.
//   2. proposal.status === 'proposed' → render the full proposal
//      (mode, voice/visual direction, pillars, cadence, narrative
//      arc, first steps) with "approve, let mag run this" and
//      "discard" actions.
//   3. proposal.status === 'approved' → terminal — the channel page
//      will already be re-rendering because operatedByMag flipped
//      to true on the same tick, so this section unmounts.
function ProposalSection({
  brand,
  kind,
  envelope,
  autoOpenIntake = false,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  envelope: ChannelTakeoverProposalEnvelope | null;
  /** Deep-link signal from the channel_onboarding_pending feed card.
   *  When true, the chat drawer mounts immediately on first render so
   *  the founder lands inside the conversation without an extra
   *  click. */
  autoOpenIntake?: boolean;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const refresh = () => refreshQueries(queryClient, ['brand-channel', brand.id, kind]);
  // After the founder approves the plan, refetch + drop them on the
  // slots tab so they watch the first draft slots generate (and the
  // ?intake=open param clears, closing the drawer).
  const handleFinalized = async () => {
    await refresh();
    await navigate({
      to: '/brand/$slug/channels/$kind',
      params: { slug: brand.slug, kind },
      search: { section: 'slots' as const },
    });
  };
  // [channel-intake] When true, the chat drawer overlay is mounted.
  // Replaces the legacy one-shot `proposeChannelTakeover` flow on
  // the default empty state — the founder chats with mag instead of
  // letting an agent draft a written plan they have to read top to
  // bottom. The legacy flow is still wired (envelope path below) as
  // a safety net for any in-flight proposal from before this shipped.
  const [intakeOpen, setIntakeOpen] = useState(autoOpenIntake);
  // Honor a later flip of autoOpenIntake — if the founder navigates
  // within the channel page (search-param changes) without unmount,
  // the drawer should still open the moment ?intake=open arrives.
  useEffect(() => {
    if (autoOpenIntake) setIntakeOpen(true);
  }, [autoOpenIntake]);

  // [s263] Proposal generation is no longer auto-fired by the meta-
  // finalize handler — connecting Meta only hooks the account up so
  // mag can READ engagement; it isn't a commitment to let mag publish.
  // The founder explicitly opts in by clicking "draft a plan" here.
  // The "still drafting" branch below survives as a safety net in case
  // a proposal IS in flight (e.g. retry after the mutation kicked off).
  const proposeMutation = useMutation({
    mutationFn: async () => proposeChannelTakeover(brand.id, kind),
    onSuccess: async () => {
      await refresh();
      toast.success(t`here's my plan — read it through.`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const approveMutation = useMutation({
    mutationFn: async () => approveChannelTakeoverProposal(brand.id, kind),
    onSuccess: async () => {
      await refresh();
      toast.success(t`i've got this one — drafting your first posts now`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const discardMutation = useMutation({
    mutationFn: async () => discardChannelTakeoverProposal(brand.id, kind),
    onSuccess: async () => {
      await refresh();
      toast.success(t`scrapped. ask me again whenever.`);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // [s263] No envelope = no takeover proposal has been drafted yet.
  // The legacy `stillDrafting` branch below was a holdover from when
  // meta-finalize auto-fired the proposal agent on a fresh OAuth
  // round-trip; that was removed (the spec §5 trust ledger doesn't
  // treat "granted Instagram-read scopes" as "i hereby authorize you
  // to publish"). So a connected-but-unproposed channel always means
  // "the founder hasn't chatted yet" — never "mag is silently
  // drafting in the background." Show the chat-with-mag CTA
  // immediately. The takeover_proposal column is preserved on the
  // table for legacy reads (envelope === 'approved' / 'discarded' /
  // 'superseded' branches below) but is no longer written from this
  // surface; the new path is intake_state on the channel.
  if (envelope === null) {
    return (
      <section className="mag-stagger-in mb-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>takeover · proposal</Trans>
        </p>
        <h3 className="mt-1 font-display text-[18px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
          <Trans>let's plan this together.</Trans>
        </h3>
        <p className="mt-2 text-[13.5px] leading-[1.5] text-[var(--ink)]/75">
          <Trans>
            quick chat — five-ish questions about what you want from {kind} in the next 30 days,
            your business shape, and how much leash you want me on. i'll write a plan from there.
            nothing gets published until you approve.
          </Trans>
        </p>
        <div className="mt-4 flex items-center justify-end">
          <button type="button" onClick={() => setIntakeOpen(true)} className="btn">
            <Trans>chat with mag</Trans>
            <span className="btn-arrow">→</span>
          </button>
        </div>
        {intakeOpen ? (
          <ChannelIntakeDrawer
            brandId={brand.id}
            channelKind={kind}
            channelLabel={kind}
            onClose={() => setIntakeOpen(false)}
            onFinalized={handleFinalized}
          />
        ) : null}
      </section>
    );
  }

  // discarded / superseded: founder can request another. Same shell
  // as the post-window empty state but with copy that acknowledges
  // the prior decision.
  if (envelope.status === 'discarded' || envelope.status === 'superseded') {
    return (
      <section className="mb-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
        <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
          {'↘'} <Trans>takeover · proposal</Trans>
        </p>
        <h3 className="mt-1 font-display text-[18px] font-bold leading-tight tracking-[-0.01em] text-[var(--ink)]">
          <Trans>want me to take another swing?</Trans>
        </h3>
        <p className="mt-2 text-[13.5px] leading-[1.5] text-[var(--ink)]/75">
          {envelope.status === 'discarded' ? (
            <Trans>
              you discarded the last plan i wrote. i can draft a fresh one — same shape, fresh read
              on your posts and brain.
            </Trans>
          ) : (
            <Trans>
              you replaced the last plan i wrote. i can draft a fresh one — same shape, fresh read
              on your posts and brain.
            </Trans>
          )}
        </p>
        <div className="mt-4 flex items-center justify-end">
          <button
            type="button"
            disabled={proposeMutation.isPending}
            onClick={() => proposeMutation.mutate()}
            className="btn disabled:opacity-60"
          >
            {proposeMutation.isPending ? t`drafting…` : t`draft a new plan`}
            <span className="btn-arrow">→</span>
          </button>
        </div>
      </section>
    );
  }

  const proposal = envelope.proposal;
  const generatedLabel = formatDateTime(envelope.generatedAt);
  const modeChipClass =
    proposal.mode === 'build'
      ? 'bg-[var(--spell)] text-[var(--ink)] border-[var(--ink)]'
      : 'bg-[var(--sage)]/15 text-[var(--sage)] border-[var(--sage)]/40';

  return (
    <section className="mag-stagger-in mb-6 rounded-2xl border-[1.5px] border-[var(--ink)] bg-white p-5 shadow-[4px_4px_0_var(--spell)] sm:p-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>mag's plan for {kind}</Trans>
          </p>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
            <Trans>drafted {generatedLabel}</Trans>
          </p>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 font-mono text-[9.5px] font-bold uppercase tracking-[0.08em] ${modeChipClass}`}
        >
          {proposal.mode === 'build' ? t`✦ build mode` : t`↗ continue mode`}
        </span>
      </header>

      {/* Everything mag wrote in the proposal — summary, directions,
          pillar labels + descriptions, the arc and the first steps — is
          generated prose (wave 5), left as she wrote it. */}
      <p className="mt-4 text-[14.5px] leading-[1.5] text-[var(--ink)]/85">{proposal.summary}</p>

      <div className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
        <ProposalBlock label={t`how i'll sound`} body={proposal.voiceDirection} />
        <ProposalBlock label={t`how i'll look`} body={proposal.visualDirection} />
      </div>

      <div className="mt-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>pillars · {proposal.cadence.postsPerWeek}/week</Trans>
        </p>
        <ul className="mt-2 flex flex-col gap-2">
          {proposal.pillars.map((p) => (
            <li
              key={p.label}
              className="rounded-xl border border-[var(--ink)]/10 bg-[var(--paper)] p-3"
            >
              <p className="flex items-baseline justify-between gap-2 text-[13.5px] font-semibold text-[var(--ink)]">
                {p.label}
                <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
                  {p.weightPercent}%
                </span>
              </p>
              <p className="mt-1 text-[13px] leading-[1.45] text-[var(--ink)]/70">
                {p.description}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} {proposal.mode === 'build' ? t`debut arc` : t`continuation arc`} ·{' '}
          {plural(proposal.narrativeArc.length, { one: '# post', other: '# posts' })}
        </p>
        <ol className="mt-2 flex flex-col gap-2">
          {proposal.narrativeArc.map((post) => (
            <li key={post.index} className="rounded-xl border border-[var(--ink)]/10 bg-white p-3">
              <div className="flex items-baseline gap-2">
                <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-[1.5px] border-[var(--ink)] bg-[var(--spell)] font-mono text-[11px] font-bold text-[var(--ink)]">
                  {post.index}
                </span>
                <p className="flex-1 text-[13.5px] font-semibold leading-tight text-[var(--ink)]">
                  {post.angle}
                </p>
              </div>
              <p className="mt-2 whitespace-pre-line text-[13px] leading-[1.5] text-[var(--ink)]/80">
                {post.sketch}
              </p>
              <p className="mt-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/45">
                {'↘'} <Trans>why:</Trans> {post.intent}
              </p>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-5">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--plum)]">
          {'↘'} <Trans>first week</Trans>
        </p>
        <ul className="mt-2 flex flex-col gap-2">
          {proposal.firstSteps.map((s) => (
            <li
              key={s.label}
              className="rounded-xl border border-dashed border-[var(--ink)]/15 bg-[var(--paper)]/40 p-3"
            >
              <p className="text-[13.5px] font-semibold text-[var(--ink)]">{s.label}</p>
              <p className="mt-1 text-[13px] leading-[1.45] text-[var(--ink)]/70">
                {s.description}
              </p>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-end gap-3 border-t border-[var(--ink)]/8 pt-4">
        <button
          type="button"
          disabled={discardMutation.isPending || approveMutation.isPending}
          onClick={() => discardMutation.mutate()}
          className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55 transition-colors duration-150 hover:text-[var(--pulse)] disabled:opacity-50"
        >
          {'↘'} {discardMutation.isPending ? t`scrapping…` : t`discard`}
        </button>
        <button
          type="button"
          disabled={approveMutation.isPending || discardMutation.isPending}
          onClick={() => approveMutation.mutate()}
          className="btn disabled:opacity-60"
        >
          {approveMutation.isPending ? t`starting up…` : t`approve, let mag run this`}
          <span className="btn-arrow">→</span>
        </button>
      </div>
    </section>
  );
}

function ProposalBlock({ label, body }: { label: string; body: string }) {
  return (
    <div className="rounded-xl border border-[var(--ink)]/10 bg-[var(--paper)] p-3">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
        {'↘'} {label}
      </p>
      <p className="mt-1.5 text-[13px] leading-[1.5] text-[var(--ink)]/80">{body}</p>
    </div>
  );
}

/** 49/N — autopilot toggle row inside ControlSection. When on, mag
 *  publishes scheduled slots without asking. When off, every post
 *  emits a slot_approval_pending card on /home for founder review. */
function ChannelAutopilotRow({
  brand,
  kind,
  channel,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  channel: BrandChannelDTO;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (next: boolean) =>
      patchBrandChannelSettings(brand.id, kind, { autopilot: next }),
    onSuccess: async (_, next) => {
      await refreshQueries(queryClient, ['brand-channel', brand.id, kind]);
      toast.success(
        next
          ? t`autopilot on. i'll publish without asking.`
          : t`got it. i'll surface every post on /home for your call first.`,
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const on = channel.autopilot;
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex-1">
        <p className="text-[13px] font-semibold text-[var(--ink)]">
          {on ? t`autopilot is on` : t`every post needs your call`}
        </p>
        <p className="mt-0.5 text-[12.5px] text-[var(--ink)]/65">
          {on
            ? t`i'll draft, render, and publish on cadence without asking. you can pause anytime.`
            : t`i'll draft + render at the right moment then ping you on /home for the final yes/no.`}
        </p>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate(!on)}
          className={`btn ${on ? 'btn-ghost' : 'btn-spell'} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {mutation.isPending ? t`updating…` : on ? t`turn autopilot off` : t`turn autopilot on`}
          {!mutation.isPending ? <span className="btn-arrow">→</span> : null}
        </button>
      </div>
    </div>
  );
}

/** 50/N — channel-level creative mode default. mag = LLM renders;
 *  designer = brief gets routed to an external designer who uploads
 *  the asset. Per-slot override available via the slot drawer. */
function ChannelCreativeModeRow({
  brand,
  kind,
  channel,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  channel: BrandChannelDTO;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (next: 'mag' | 'designer') =>
      patchBrandChannelSettings(brand.id, kind, { creativeMode: next }),
    onSuccess: async (_, next) => {
      await refreshQueries(queryClient, ['brand-channel', brand.id, kind]);
      toast.success(
        next === 'designer'
          ? t`got it. i'll send the brief to your designer at the review window.`
          : t`back to me. i'll render every asset from now on.`,
      );
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const designerMode = channel.creativeMode === 'designer';
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex-1">
        <p className="text-[13px] font-semibold text-[var(--ink)]">
          {designerMode ? t`designer in the loop` : t`mag renders every asset`}
        </p>
        <p className="mt-0.5 text-[12.5px] text-[var(--ink)]/65">
          {designerMode
            ? t`i write the caption + brief; your designer uploads the asset. i'll override per-slot if a routine post doesn't need them.`
            : t`i pull the asset from the creative pipeline. flip this if you have a designer in the loop and want them to render.`}
        </p>
      </div>
      <div className="flex justify-end">
        <button
          type="button"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate(designerMode ? 'mag' : 'designer')}
          className={`btn ${designerMode ? 'btn-ghost' : 'btn-spell'} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {mutation.isPending
            ? t`updating…`
            : designerMode
              ? t`let mag render everything`
              : t`route to designer`}
          {!mutation.isPending ? <span className="btn-arrow">→</span> : null}
        </button>
      </div>
    </div>
  );
}

/** [designer-role] Designer routing row. Visible only when
 *  creativeMode='designer'. Replaces the old free-text email field
 *  with a picker of team members who hold the 'designer' brand role
 *  + an "+ invite a designer" inline form. Founders who still want
 *  to brief an external designer (no magig account) can flip to the
 *  free-text fallback under "use an external email".
 *
 *  Wire shape:
 *    - When designerUserId is set, slot-jit-prep + slot-designer-
 *      upload read the email from the joined user row (api refreshes
 *      designerEmail on each settings write).
 *    - When designerUserId is null and designerEmail is set, the
 *      free-text email is used unchanged — backwards compat with the
 *      pre-picker flow. */
function ChannelDesignerRouteRow({
  brand,
  kind,
  channel,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  channel: BrandChannelDTO;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const designersQ = useQuery({
    queryKey: ['brand-designers', brand.id] as const,
    queryFn: () => fetchBrandDesigners(brand.id),
    staleTime: 60_000,
  });
  const designers: DesignerOption[] = designersQ.data ?? [];
  const currentMember =
    channel.designerUserId !== null
      ? (designers.find((d) => d.userId === channel.designerUserId) ?? null)
      : null;
  const externalEmail = channel.designerUserId === null ? (channel.designerEmail ?? '') : '';

  const pickMutation = useMutation({
    mutationFn: async (next: { userId: string | null; email: string | null }) =>
      patchBrandChannelSettings(brand.id, kind, {
        designerUserId: next.userId,
        // Setting userId server-side refreshes email from the user row.
        // When clearing back to external, the email field becomes
        // founder-managed again.
        ...(next.userId === null ? { designerEmail: next.email } : {}),
      }),
    onSuccess: async (_, next) => {
      await refreshQueries(queryClient, ['brand-channel', brand.id, kind]);
      if (next.userId === null && next.email === null) {
        toast.success(t`designer cleared.`);
      } else if (next.userId !== null) {
        toast.success(t`ok. i'll route briefs to them.`);
      } else {
        toast.success(t`i'll send briefs to ${next.email}.`);
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const [mode, setMode] = useState<'team' | 'external'>(
    channel.designerUserId !== null
      ? 'team'
      : externalEmail.length > 0
        ? 'external'
        : designers.length > 0
          ? 'team'
          : 'external',
  );

  // Auto-flip mode once the designers list resolves — if the channel
  // already has a userId set, we should land on 'team' even if the
  // initial render happened with an empty list.
  useEffect(() => {
    if (channel.designerUserId !== null && mode !== 'team') setMode('team');
  }, [channel.designerUserId, mode]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex-1">
          <p className="text-[13px] font-semibold text-[var(--ink)]">
            <Trans>designer</Trans>
          </p>
          <p className="mt-0.5 text-[12.5px] text-[var(--ink)]/65">
            {currentMember ? (
              <Trans>
                briefs go to{' '}
                <span className="font-semibold">{currentMember.name ?? currentMember.email}</span>{' '}
                <span className="font-mono">&lt;{currentMember.email}&gt;</span>. they'll get a ping
                inside magig when each slot needs an asset.
              </Trans>
            ) : externalEmail ? (
              <Trans>
                briefs go to <span className="font-mono">{externalEmail}</span> — magic-link upload,
                no magig account required.
              </Trans>
            ) : (
              <span className="font-mono text-[var(--pulse)]">
                {'↘'}{' '}
                <Trans>
                  no designer set — slots flagged for designer mode will wait without a ping.
                </Trans>
              </span>
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
          <button
            type="button"
            onClick={() => setMode('team')}
            className={`rounded-full px-2 py-1 transition ${
              mode === 'team'
                ? 'border border-[var(--ink)] bg-[var(--spell)] text-[var(--ink)]'
                : 'hover:text-[var(--ink)]'
            }`}
          >
            <Trans>team</Trans>
          </button>
          <button
            type="button"
            onClick={() => setMode('external')}
            className={`rounded-full px-2 py-1 transition ${
              mode === 'external'
                ? 'border border-[var(--ink)] bg-[var(--spell)] text-[var(--ink)]'
                : 'hover:text-[var(--ink)]'
            }`}
          >
            <Trans>external email</Trans>
          </button>
        </div>
      </div>

      {mode === 'team' ? (
        <DesignerTeamPicker
          brand={brand}
          designers={designers}
          loading={designersQ.isLoading}
          current={currentMember}
          onPick={(userId) => pickMutation.mutate({ userId, email: null })}
          onClear={() => pickMutation.mutate({ userId: null, email: null })}
          onInvited={async () => {
            await refreshQueries(queryClient, ['brand-designers', brand.id]);
          }}
          busy={pickMutation.isPending}
        />
      ) : (
        <DesignerExternalEmail
          channel={channel}
          onSave={(email) => pickMutation.mutate({ userId: null, email })}
          busy={pickMutation.isPending}
        />
      )}
    </div>
  );
}

function DesignerTeamPicker({
  brand,
  designers,
  loading,
  current,
  onPick,
  onClear,
  onInvited,
  busy,
}: {
  brand: Brand;
  designers: DesignerOption[];
  loading: boolean;
  current: DesignerOption | null;
  onPick: (userId: string) => void;
  onClear: () => void;
  onInvited: () => void | Promise<void>;
  busy: boolean;
}) {
  const { t } = useLingui();
  const [inviteOpen, setInviteOpen] = useState(false);
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <select
          value={current?.userId ?? ''}
          onChange={(e) => {
            const userId = e.target.value;
            if (userId.length === 0) onClear();
            else onPick(userId);
          }}
          disabled={busy || loading || (designers.length === 0 && current === null)}
          className="field-input min-w-[240px] text-left text-[13px] text-[var(--ink)]"
        >
          <option value="">{loading ? t`loading…` : t`— pick a team designer —`}</option>
          {designers.map((d) => (
            <option key={d.userId} value={d.userId}>
              {d.name ?? d.email}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => setInviteOpen((v) => !v)}
          className="btn btn-ghost text-[11px]"
        >
          {inviteOpen ? t`cancel invite` : t`+ invite a designer`}
        </button>
      </div>
      {!loading && designers.length === 0 ? (
        <p className="text-right text-[11.5px] text-[var(--ink)]/55">
          <Trans>no team designers yet — invite one to populate the picker.</Trans>
        </p>
      ) : null}
      {inviteOpen ? (
        <InviteDesignerInline
          brandId={brand.id}
          onInvited={async () => {
            setInviteOpen(false);
            await onInvited();
          }}
        />
      ) : null}
    </div>
  );
}

function InviteDesignerInline({
  brandId,
  onInvited,
}: {
  brandId: string;
  onInvited: () => void | Promise<void>;
}) {
  const { t } = useLingui();
  const [email, setEmail] = useState('');
  const mutation = useMutation({
    mutationFn: async (e: string) =>
      createInvitation({
        email: e,
        workspaceRole: 'member',
        brandGrants: [{ brandId, role: 'designer' }],
      }),
    onSuccess: async (res) => {
      toast.success(
        t`invite sent to ${res.invitation.email}.`,
        // We don't surface the accept URL here — the email handles it.
        // If the founder needs to copy the link they can do it from the
        // /team page where the raw token is still in scope on the
        // create response.
      );
      await onInvited();
      setEmail('');
    },
    onError: (err: Error) => {
      if (err instanceof InvitationApiError && err.code === 'duplicate_pending_invite') {
        toast.error(t`an invite is already pending for that email.`);
        return;
      }
      toast.error(err.message);
    },
  });
  const trimmed = email.trim();
  const valid = trimmed.length > 0 && /\S+@\S+\.\S+/.test(trimmed);
  return (
    <div className="rounded-xl border border-[var(--ink)]/15 bg-[var(--spell-soft)]/30 p-3">
      <p className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--ink)]/55">
        {'↘'} <Trans>invite a designer</Trans>
      </p>
      <p className="mt-1 text-[12.5px] text-[var(--ink)]/65">
        <Trans>
          they'll get an email with an accept link. on accept they land as a designer on this brand
          — no other access, just slot uploads.
        </Trans>
      </p>
      <div className="mt-2 flex flex-wrap items-center justify-end gap-2">
        <input
          type="email"
          placeholder={t`designer@studio.com`}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={mutation.isPending}
          className="field-input min-w-[220px] text-left font-mono text-[13px] text-[var(--ink)]"
        />
        <button
          type="button"
          onClick={() => mutation.mutate(trimmed.toLowerCase())}
          disabled={!valid || mutation.isPending}
          className="btn disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? t`sending…` : t`send invite`}
          {!mutation.isPending ? <span className="btn-arrow">→</span> : null}
        </button>
      </div>
    </div>
  );
}

function DesignerExternalEmail({
  channel,
  onSave,
  busy,
}: {
  channel: BrandChannelDTO;
  onSave: (email: string | null) => void;
  busy: boolean;
}) {
  const { t } = useLingui();
  const [email, setEmail] = useState<string>(channel.designerEmail ?? '');
  const dirty = email.trim() !== (channel.designerEmail ?? '');
  const valid = email.trim().length === 0 || /\S+@\S+\.\S+/.test(email.trim());
  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <input
        type="email"
        placeholder={t`designer@studio.com`}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="field-input min-w-[240px] text-left font-mono text-[13px] text-[var(--ink)]"
      />
      <button
        type="button"
        disabled={busy || !dirty || !valid}
        onClick={() => onSave(email.trim().length === 0 ? null : email.trim().toLowerCase())}
        className="btn btn-ghost disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? t`saving…` : dirty ? t`save` : t`saved`}
        {!busy && dirty ? <span className="btn-arrow">→</span> : null}
      </button>
    </div>
  );
}

/** 49/N — approval lead-time row. Only meaningful when autopilot is
 *  off (the JIT trigger fires at scheduled_for - leadTimeHours). On
 *  autopilot=true channels the input still renders but reads as a
 *  hint of what the consent flow would do; founder can pre-tune
 *  before flipping autopilot off later. */
function ChannelLeadTimeRow({
  brand,
  kind,
  channel,
}: {
  brand: Brand;
  kind: BrandChannelKind;
  channel: BrandChannelDTO;
}) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const [hours, setHours] = useState<number>(channel.approvalLeadTimeHours);
  const mutation = useMutation({
    mutationFn: async (next: number) =>
      patchBrandChannelSettings(brand.id, kind, { approvalLeadTimeHours: next }),
    onSuccess: async (_, next) => {
      await refreshQueries(queryClient, ['brand-channel', brand.id, kind]);
      toast.success(t`review window: ${next}h before each post.`);
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const dirty = hours !== channel.approvalLeadTimeHours;
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div className="flex-1">
        <p className="text-[13px] font-semibold text-[var(--ink)]">
          <Trans>review window</Trans>
        </p>
        <p className="mt-0.5 text-[12.5px] text-[var(--ink)]/65">
          {channel.autopilot
            ? t`if you ever turn autopilot off, i'll ping you ${channel.approvalLeadTimeHours}h before each post.`
            : t`i ping you ${channel.approvalLeadTimeHours}h before each scheduled post. shorter = tighter loop, longer = more batch-able.`}
        </p>
      </div>
      <div className="flex flex-col items-end gap-3 sm:flex-row sm:items-end">
        <label className="flex items-center gap-2 font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-[var(--ink)]/55">
          <Trans>hours</Trans>
          <input
            type="number"
            min={1}
            max={168}
            value={hours}
            onChange={(e) => setHours(Math.max(1, Math.min(168, Number(e.target.value))))}
            className="field-input w-20 text-right font-mono text-[14px] font-semibold text-[var(--ink)]"
          />
        </label>
        <button
          type="button"
          disabled={mutation.isPending || !dirty}
          onClick={() => mutation.mutate(hours)}
          className="btn btn-ghost disabled:cursor-not-allowed disabled:opacity-50"
        >
          {mutation.isPending ? t`saving…` : dirty ? t`save` : t`saved`}
          {!mutation.isPending && dirty ? <span className="btn-arrow">→</span> : null}
        </button>
      </div>
    </div>
  );
}
